import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Trash2, FileText, Mail, Calendar, Globe, FileSignature, Camera, Download, Upload, FileDown, Scale, Contact } from "lucide-react";
import { toast } from "sonner";
import { downloadSubmissionPDF } from "../lib/submissionPdf";
import { PrivatePhoto, hasPhoto } from "../lib/privatePhotos";

const COLLAB_STATUSES = ["new", "reviewed", "accepted", "declined", "archived"];
const APP_STATUSES = ["new", "reviewed", "approved", "rejected", "archived"];
const STATUS_COLORS = {
  new: "text-[color:var(--gio-primary)] border-[color:var(--gio-primary)]",
  reviewed: "text-[color:var(--gio-secondary)] border-[color:var(--gio-secondary)]",
  accepted: "text-green-400 border-green-700",
  approved: "text-green-400 border-green-700",
  declined: "text-zinc-500 border-zinc-700",
  rejected: "text-zinc-500 border-zinc-700",
  archived: "text-zinc-700 border-zinc-800",
};

export default function Submissions() {
  const [tab, setTab] = useState("collab"); // collab | modelapply
  const [collabs, setCollabs] = useState([]);
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [hasMoreApps, setHasMoreApps] = useState(false);
  const [loadingMoreApps, setLoadingMoreApps] = useState(false);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([
        api.get("/api/collab-submissions/"),
        api.get("/api/model-applications/", { params: { limit: 50 } }),
      ]);
      setCollabs(c.data);
      setApps(a.data);
      setHasMoreApps(a.data.length === 50);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load submissions");
    }
  };
  useEffect(() => { load(); }, []);

  const loadMoreApps = async () => {
    if (loadingMoreApps) return;
    setLoadingMoreApps(true);
    try {
      const response = await api.get("/api/model-applications/", { params: { limit: 50, offset: apps.length } });
      setApps((current) => [...current, ...response.data]);
      setHasMoreApps(response.data.length === 50);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load older applications");
    } finally {
      setLoadingMoreApps(false);
    }
  };

  const items = tab === "collab" ? collabs : apps;

  const downloadCSV = async () => {
    try {
      const path = tab === "collab" ? "/api/collab-submissions/export.csv" : "/api/exports/model-applications.csv";
      const r = await api.get(path, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `gioagent-${tab}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };

  const onImportFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    const text = await f.text();
    setImportText(text);
  };

  const runImport = async () => {
    if (!importText.trim()) { toast.error("Paste CSV text or upload a file first"); return; }
    setImporting(true);
    try {
      const r = await api.post("/api/collab-submissions/import", { csv_text: importText });
      toast.success(`Imported · created ${r.data.submissions_created} · updated ${r.data.submissions_updated} · CRM created ${r.data.crm_created}`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} row(s) had errors — see console`);
      setShowImport(false);
      setImportText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const updateStatus = async (id, status) => {
    const url = tab === "collab" ? `/api/collab-submissions/${id}` : `/api/model-applications/${id}`;
    await api.put(url, { status });
    toast.success(`Marked as ${status}`);
    load();
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
  };

  const saveNotes = async (id, admin_notes) => {
    const url = tab === "collab" ? `/api/collab-submissions/${id}` : `/api/model-applications/${id}`;
    await api.put(url, { admin_notes });
    toast.success("Notes saved");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    const url = tab === "collab" ? `/api/collab-submissions/${id}` : `/api/model-applications/${id}`;
    await api.delete(url);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const switchTab = (t) => { setTab(t); setSelected(null); };

  return (
    <AppLayout title="Submissions" overline="130 · giopilos.com/form + /modelapply · PUBLIC INTAKE">
      {/* Tabs */}
      <div className="flex gap-2 mb-6 items-center" data-testid="sub-tabs">
        <TabBtn active={tab === "collab"} onClick={() => switchTab("collab")} testid="sub-tab-collab" count={collabs.length} icon={FileSignature} label="Collab Forms" sub="/form" />
        <TabBtn active={tab === "modelapply"} onClick={() => switchTab("modelapply")} testid="sub-tab-modelapply" count={apps.length} icon={Camera} label="Model Applications" sub="/modelapply" />
        <div className="ml-auto flex gap-2">
          {tab === "collab" && (
            <button onClick={() => setShowImport(true)} data-testid="sub-import-button" className="btn-ghost inline-flex items-center gap-2 text-sm">
              <Upload size={14} /> Import CSV
            </button>
          )}
          <button onClick={downloadCSV} data-testid="sub-export-button" className="btn-ghost inline-flex items-center gap-2 text-sm">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {showImport && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-4" onClick={() => setShowImport(false)} data-testid="import-modal">
          <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full p-6 rounded-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-[color:var(--gio-primary)]">IMPORT · COLLAB SUBMISSIONS · CSV</div>
                <h2 className="font-heading text-xl mt-1">Bulk import from CSV</h2>
                <div className="overline mt-1 text-zinc-500">Each row creates a submission AND a linked CRM record. Idempotent on email.</div>
              </div>
            </div>
            <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded mb-3 cursor-pointer hover:bg-zinc-900/50"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }} data-testid="import-file-label">
              <FileDown size={18} />
              <span className="text-[11px] font-bold uppercase mt-1">Upload .csv file</span>
              <input type="file" accept=".csv,text/csv" onChange={onImportFile} className="hidden" data-testid="import-file-input" />
            </label>
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={8} placeholder="...or paste CSV text here. First row = header (full_name, email, phone, country, city, twitter_handle, ...)"
              data-testid="import-textarea"
              className="w-full bg-black border border-zinc-900 text-white p-3 text-xs font-mono-ui focus:outline-none focus:border-[color:var(--gio-primary)]" />
            <div className="flex gap-3 mt-4">
              <button onClick={runImport} disabled={importing} data-testid="import-submit"
                className="btn-primary flex-1 disabled:opacity-50">{importing ? "Importing..." : "Import"}</button>
              <button onClick={() => { setShowImport(false); setImportText(""); }} data-testid="import-cancel" className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6" data-testid="submissions-root">
        <section className="border border-zinc-900 bg-zinc-950">
          <div className="overline p-4 border-b border-zinc-900">{items.length} SUBMISSION{items.length === 1 ? "" : "S"}</div>
          {items.length === 0 && (
            <div className="p-12 text-center text-zinc-600 text-sm">
              Nothing yet. Share <span className="text-[color:var(--gio-primary)]">{tab === "collab" ? "giopilos.com/form" : "giopilos.com/modelapply"}</span> on this app.
            </div>
          )}
          <div className="max-h-[75vh] overflow-y-auto">
            {items.map((s) => (
              <div key={s.id} onClick={() => setSelected(s)} data-testid={`sub-row-${s.id}`}
                className={`group p-4 border-b border-zinc-900/60 cursor-pointer transition ${selected?.id === s.id ? "bg-zinc-900/60" : "hover:bg-zinc-900/30"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-white font-medium">{tab === "collab" ? s.full_name : s.name}</div>
                    <div className="overline mt-1 flex items-center gap-2"><Mail size={10} /> {s.email}</div>
                    {tab === "collab" && (s.city || s.country) && <div className="overline mt-1 flex items-center gap-2"><Globe size={10} /> {[s.city, s.country].filter(Boolean).join(", ")}</div>}
                    {tab === "modelapply" && s.location && <div className="overline mt-1 flex items-center gap-2"><Globe size={10} /> {s.location}</div>}
                    {tab === "modelapply" && s.role_position && <div className="overline mt-1 text-[color:var(--gio-secondary)]">{s.role_position}</div>}
                  </div>
                  <span className={`text-[10px] font-mono-ui uppercase tracking-wider border px-2 py-0.5 ${STATUS_COLORS[s.status] || "border-zinc-800"}`}>{s.status}</span>
                </div>
                <div className="overline mt-2 flex items-center gap-2 text-zinc-600"><Calendar size={10} /> {s.submitted_at?.slice(0, 10)}</div>
              </div>
            ))}
          </div>
          {tab === "modelapply" && hasMoreApps && <button type="button" onClick={loadMoreApps} disabled={loadingMoreApps} className="w-full border-t border-zinc-900 p-3 text-sm text-zinc-300 hover:bg-zinc-900/50 disabled:opacity-50">
            {loadingMoreApps ? "Loading…" : "Load older applications"}
          </button>}
        </section>

        <section>
          {!selected && (
            <div className="border border-dashed border-zinc-900 p-12 text-center text-zinc-600 text-sm">
              Pick a submission to view details.
            </div>
          )}
          {selected && (tab === "collab" ? <CollabDetail s={selected} updateStatus={updateStatus} saveNotes={saveNotes} remove={remove} /> : <AppDetail s={selected} updateStatus={updateStatus} saveNotes={saveNotes} remove={remove} />)}
        </section>
      </div>
    </AppLayout>
  );
}

function TabBtn({ active, onClick, testid, count, icon: Icon, label, sub }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-3 px-4 py-3 border transition ${active ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)] bg-[color:var(--gio-primary)]/10" : "border-zinc-900 text-zinc-500 hover:text-white"}`}>
      <Icon size={16} />
      <div className="text-left">
        <div className="font-mono-ui text-[11px] tracking-wider uppercase">{label}</div>
        <div className="text-[10px] text-zinc-600">{sub}</div>
      </div>
      <span className="ml-2 px-2 py-0.5 bg-black border border-zinc-800 font-mono-ui text-[10px]">{count}</span>
    </button>
  );
}

function CollabDetail({ s, updateStatus, saveNotes, remove }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950 p-6" data-testid="sub-detail">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="overline text-[color:var(--gio-primary)]">{s.locale?.toUpperCase() === "EL" ? "SIGNED IN ΕΛΛΗΝΙΚΑ" : "SIGNED IN ENGLISH"}</div>
          <h2 className="font-heading text-3xl mt-1">{s.full_name}</h2>
          <div className="overline mt-1">{s.email} · {s.phone || "no phone"}</div>
        </div>
        <div className="flex gap-2 items-start">
          <button onClick={() => downloadSubmissionPDF(s, "collab")} data-testid="sub-pdf-button"
            className="btn-ghost inline-flex items-center gap-2 text-xs">
            <FileDown size={14} /> PDF
          </button>
          <button 
            onClick={async () => {
              try {
                toast.info('Generating PDF...');
                // Generate PDF and send to Legal Vault
                const pdfBlob = await downloadSubmissionPDF(s, "collab", true); // true = return blob
                
                if (!pdfBlob) {
                  throw new Error('PDF generation failed - no blob returned');
                }
                
                toast.info('Sending to Legal Vault...');
                const formData = new FormData();
                formData.append('file', pdfBlob, `${s.full_name}_Collab_Form.pdf`);
                formData.append('title', `${s.full_name} - Collab Form`);
                formData.append('doc_type', 'form');
                formData.append('counterparty', s.full_name);
                formData.append('counterparty_crm_id', s.linked_crm_id || '');
                
                await api.post('/api/legal/upload', formData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('✓ Successfully sent to Legal Vault');
              } catch (e) {
                console.error('Send to Legal error:', e);
                toast.error(`Failed: ${e.message || 'Unknown error'}`);
              }
            }}
            data-testid="sub-send-legal"
            className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-2 transition-all shadow-lg shadow-purple-500/30">
            <Scale size={14} /> Send to Legal
          </button>
          <button onClick={() => remove(s.id)} data-testid="sub-delete-button" className="text-zinc-600 hover:text-[color:var(--gio-primary)]"><Trash2 size={16} /></button>
        </div>
      </div>
      {s.linked_crm_id && (
        <div className="mb-6 p-3 rounded flex items-center gap-3 flex-wrap"
          style={{ background: "var(--gio-primary-soft)", border: "1px solid var(--gio-primary)" }}
          data-testid="sub-crm-link">
          <div className="flex-1 min-w-0">
            <div className="overline" style={{ color: "var(--gio-primary)" }}>✅ AUTO-LINKED TO CRM</div>
            <div className="text-[12px] mt-0.5" style={{ color: "var(--gio-muted)" }}>Submission was automatically added to your CRM pipeline as <strong>{s.full_name}</strong>.</div>
          </div>
          <a href={`/crm?focus=${s.linked_crm_id}`} data-testid="sub-open-crm"
            className="btn-primary text-[11px] inline-flex items-center gap-1.5">
            Open in CRM →
          </a>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-6">
        {COLLAB_STATUSES.map((st) => (
          <button key={st} onClick={() => updateStatus(s.id, st)} data-testid={`sub-status-${st}`}
            className={`px-3 py-1.5 text-[11px] font-mono-ui uppercase tracking-wider border transition ${s.status === st ? STATUS_COLORS[st] : "border-zinc-800 text-zinc-500 hover:text-white"}`}>→ {st}</button>
        ))}
      </div>
      <Info label="Legal name" value={s.legal_name} />
      <Info label="Date of birth" value={s.date_of_birth} />
      <Info label="Pronouns" value={s.pronouns} />
      <Info label="Address" value={s.address} />
      <Info label="Location" value={[s.city, s.state, s.country].filter(Boolean).join(", ")} />
      <div className="mt-6">
        <div className="overline text-[color:var(--gio-primary)] mb-2 flex items-center gap-2"><FileText size={12} /> SOCIALS</div>
        <Info label="Twitter" value={s.twitter_handle && `@${s.twitter_handle.replace(/^@/, "")}`} />
        <Info label="Instagram" value={s.instagram_handle && `@${s.instagram_handle.replace(/^@/, "")}`} />
        <Info label="OnlyFans" value={s.onlyfans_link} />
        <Info label="JustForFans" value={s.justforfans_link} />
        <Info label="Other" value={s.other_links} />
      </div>
      <div className="mt-6">
        <div className="overline text-[color:var(--gio-primary)] mb-2">LEGAL</div>
        <Info label="Agreed to terms" value={s.agreed_terms ? "✅ Yes" : "❌ No"} />
        <Info label="Signed as (typed)" value={s.signature_name} />
        <Info label="Submitted at" value={s.submitted_at?.slice(0, 19).replace("T", " ")} />
      </div>
      {["id_front", "id_back", "selfie"].some((k) => hasPhoto(s, "collab", k)) && (
        <div className="mt-6">
          <div className="overline text-[color:var(--gio-primary)] mb-2">ID PHOTOS <span className="text-zinc-600 normal-case">· private, short-lived links</span></div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: "id_front", label: "Front" },
              { k: "id_back", label: "Back" },
              { k: "selfie", label: "Selfie" },
            ].map(({ k, label }) => hasPhoto(s, "collab", k) ? (
              <div key={k}>
                <div className="overline mb-1">{label}</div>
                <PrivatePhoto kind="collab" id={s.id} slot={k} alt={label} testid={`sub-${k}_url`}
                  className="w-full aspect-[3/4] object-cover border border-zinc-800 hover:border-[color:var(--gio-primary)]" />
              </div>
            ) : (
              <div key={k} className="aspect-[3/4] border border-dashed border-zinc-800 flex items-center justify-center text-[10px] text-zinc-600">{label}: missing</div>
            ))}
          </div>
        </div>
      )}
      {s.signature_drawn_data_url && (
        <div className="mt-6">
          <div className="overline text-[color:var(--gio-primary)] mb-2">DRAWN SIGNATURE</div>
          <img src={s.signature_drawn_data_url} alt="drawn signature"
            className="bg-zinc-900 border border-zinc-800 p-2 max-w-md w-full"
            data-testid="sub-drawn-sig" />
        </div>
      )}
      <AdminNotes s={s} saveNotes={saveNotes} />
    </div>
  );
}

function AppDetail({ s, updateStatus, saveNotes, remove }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950 p-6" data-testid="app-detail">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="overline text-[color:var(--gio-primary)]">MODEL APPLICATION · /modelapply</div>
          <h2 className="font-heading text-3xl mt-1">{s.name}</h2>
          <div className="overline mt-1">{s.email}</div>
        </div>
        <div className="flex gap-2 items-start">
          <button onClick={() => downloadSubmissionPDF(s, "modelapply")} data-testid="app-pdf-button"
            className="btn-ghost inline-flex items-center gap-2 text-xs">
            <FileDown size={14} /> PDF
          </button>
          <button 
            onClick={async () => {
              try {
                const pdfBlob = await downloadSubmissionPDF(s, "modelapply", true);
                const formData = new FormData();
                formData.append('file', pdfBlob, `${s.name}_Model_Application.pdf`);
                formData.append('title', `${s.name} - Model Application`);
                formData.append('doc_type', 'form');
                formData.append('counterparty', s.name);
                
                await api.post('/api/legal/upload', formData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('✓ Sent to Legal Vault');
              } catch (e) {
                toast.error('Failed to send to Legal Vault');
              }
            }}
            data-testid="app-send-legal"
            className="btn-primary inline-flex items-center gap-2 text-xs">
            <Scale size={14} /> Send to Legal
          </button>
          <button onClick={() => remove(s.id)} data-testid="app-delete-button" className="text-zinc-600 hover:text-[color:var(--gio-primary)]"><Trash2 size={16} /></button>
        </div>
      </div>
      {s.linked_crm_id ? (
        <div className="mb-6 p-3 rounded flex items-center gap-3 flex-wrap"
          style={{ background: "var(--gio-primary-soft)", border: "1px solid var(--gio-primary)" }}
          data-testid="app-crm-link">
          <div className="flex-1 min-w-0">
            <div className="overline" style={{ color: "var(--gio-primary)" }}>✅ OPTED-IN · LINKED TO CRM</div>
            <div className="text-[12px] mt-0.5" style={{ color: "var(--gio-muted)" }}>Applicant chose to be added to your CRM roster.</div>
          </div>
          <a href={`/crm?focus=${s.linked_crm_id}`} data-testid="app-open-crm"
            className="btn-primary text-[11px] inline-flex items-center gap-1.5">Open in CRM →</a>
        </div>
      ) : (
        <div className="mb-6 p-3 rounded flex items-center gap-3 flex-wrap border border-zinc-800 bg-zinc-950"
          data-testid="app-no-crm">
          <div className="flex-1 min-w-0">
            <div className="overline text-zinc-500">⚪ NOT IN CRM</div>
            <div className="text-[12px] mt-0.5 text-zinc-500">
              {s.add_to_crm ? "Opt-in received but CRM link failed — check backend logs." : "Applicant did not opt-in to be added to your CRM roster."}
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                const r = await api.post(`/api/model-applications/${s.id}/add-to-crm`);
                toast.success(`✓ Added to CRM as ${r.data.crm_name}`);
                window.location.reload(); // Reload to show the linked CRM
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to add to CRM');
              }
            }}
            data-testid="app-add-crm"
            className="btn-primary text-xs inline-flex items-center gap-1.5">
            <Contact size={14} /> Add to CRM
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-6">
        {APP_STATUSES.map((st) => (
          <button key={st} onClick={() => updateStatus(s.id, st)} data-testid={`app-status-${st}`}
            className={`px-3 py-1.5 text-[11px] font-mono-ui uppercase tracking-wider border transition ${s.status === st ? STATUS_COLORS[st] : "border-zinc-800 text-zinc-500 hover:text-white"}`}>→ {st}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-6">
        {["face_photo","front_nude_photo","back_nude_photo","dick_photo","ass_photo"].map((k) => hasPhoto(s, "modelapply", k) ? (
          <div key={k}>
            <div className="overline mb-1">{k.replace(/_/g, " ").replace("photo","")}</div>
            <PrivatePhoto kind="modelapply" id={s.id} slot={k} alt={k} testid={`app-photo-${k}`}
              className="w-full aspect-[3/4] object-cover border border-zinc-800 hover:border-[color:var(--gio-primary)]" />
          </div>
        ) : null)}
      </div>
      <Info label="X (Twitter)" value={s.twitter_handle && `@${s.twitter_handle.replace(/^@/, "")}`} />
      <Info label="Body Type" value={s.body_type} />
      <Info label="Dick Size" value={s.dick_size} />
      <Info label="Languages" value={s.languages} />
      <Info label="OnlyFans" value={`${s.onlyfans_status === "need_to_create" ? "🆕 Need to create" : ""} ${s.onlyfans_link}`.trim()} />
      <Info label="JustForFans" value={`${s.justforfans_status === "need_to_create" ? "🆕 Need to create" : ""} ${s.justforfans_link}`.trim()} />
      <Info label="Role / Position" value={s.role_position} />
      <Info label="Location" value={s.location} />
      <Info label="Content Types" value={s.content_types} />
      <Info label="Availability" value={s.availability} />
      <Info label="Kinks / Interests" value={s.interests_kinks} />
      <Info label="Boundaries" value={s.boundaries} />
      <Info label="Additional Notes" value={s.additional_notes} />
      <Info label="Submitted at" value={s.submitted_at?.slice(0, 19).replace("T", " ")} />
      <AdminNotes s={s} saveNotes={saveNotes} />
    </div>
  );
}

function AdminNotes({ s, saveNotes }) {
  return (
    <div className="mt-8">
      <div className="overline mb-2">ADMIN NOTES</div>
      <textarea defaultValue={s.admin_notes || ""}
        onBlur={(e) => e.target.value !== (s.admin_notes || "") && saveNotes(s.id, e.target.value)}
        rows={4} data-testid="sub-notes"
        className="w-full bg-black border border-zinc-900 text-white p-3 text-sm focus:outline-none focus:border-[color:var(--gio-primary)]"
        placeholder="Your private notes about this applicant..." />
      <div className="overline text-zinc-700 mt-1">auto-save on blur</div>
    </div>
  );
}

function Info({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-zinc-900/50 text-sm">
      <span className="overline text-zinc-500">{label}</span>
      <span className="text-white text-right break-all">{value}</span>
    </div>
  );
}
